import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

import {
  ensureSession,
  getUserInfo,
  getUserPreferences,
} from "../authService";
import { markLoginLatency, reportLoginLatencyTrace } from "../lib/loginLatency";

const AuthContext = createContext();
export const AUTH_RESTORE_TIMEOUT_MS = 3000;

export function withAuthRestoreDeadline(promise, timeoutMs = AUTH_RESTORE_TIMEOUT_MS) {
  let timeoutId;
  const deadline = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("Secure session check timed out")), timeoutMs);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timeoutId));
}

function getLoginGraceActive() {
  try {
    const raw = sessionStorage.getItem("br_login_grace_until");
    const until = Number(raw || 0);
    return until > Date.now();
  } catch {
    return false;
  }
}

function clearLoginGrace() {
  try {
    sessionStorage.removeItem("br_login_grace_until");
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,

    preferences: null,
    prefsLoading: false,
    prefsError: null,

    initialized: false,
  });

  const cancelledRef = useRef(false);

  const isRealUser = useCallback((u) => {
    if (!u || typeof u !== "object") return false;

    const sub = (u.sub || u.username || "").toString().trim();
    const success = u.success === true;
    const authedFlag = u.authenticated === true;

    if (authedFlag && sub && sub !== "anon") return true;
    if (success && sub && sub !== "anon") return true;

    return false;
  }, []);

  const checkAuth = useCallback(async () => {
    markLoginLatency('auth_restore_started');
    const commit = (partial) => {
      if (!cancelledRef.current) {
        setAuthState((prev) => ({ ...prev, ...partial }));
      }
    };

    try {
      // The mediation SID and cookie-backed identity are independent requests.
      // Start both together, but let only the real cookie identity check gate
      // rendering so an optional SID request cannot delay every refresh.
      // The deadline keeps a stalled network request from holding a private
      // route indefinitely. The route remains visually quiet and fail-closed;
      // local browser state is never treated as authentication.
      ensureSession().catch(() => null);
      const u0 = await withAuthRestoreDeadline(getUserInfo().catch(() => null));
      markLoginLatency('auth_restore_complete');

      if (isRealUser(u0)) {
        clearLoginGrace();
        commit({
          isAuthenticated: true,
          user: u0,
          loading: false,
          error: null,
          initialized: true,
        });
        return;
      }

      const graceActive = getLoginGraceActive();

      setAuthState((prev) => {
        if (prev.isAuthenticated && prev.user && graceActive) {
          return {
            ...prev,
            loading: false,
            error: null,
            initialized: true,
          };
        }

        return {
          ...prev,
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
          initialized: true,
        };
      });
    } catch (err) {
      const graceActive = getLoginGraceActive();

      setAuthState((prev) => {
        if ((prev.isAuthenticated && prev.user) || graceActive) {
          return {
            ...prev,
            loading: false,
            error: null,
            initialized: true,
          };
        }

        return {
          ...prev,
          isAuthenticated: false,
          user: null,
          loading: false,
          error: err?.message || "Auth failed",
          initialized: true,
        };
      });
    }
  }, [isRealUser]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!authState.initialized) {
      checkAuth();
    }

    return () => {
      cancelledRef.current = true;
    };
  }, [authState.initialized, checkAuth]);

  useEffect(() => {
    if (
      authState.isAuthenticated &&
      authState.user &&
      !authState.prefsLoading &&
      authState.preferences === null &&
      !authState.prefsError
    ) {
      setAuthState((prev) => ({
        ...prev,
        prefsLoading: true,
        prefsError: null,
      }));

      markLoginLatency('preferences_load_started');

      getUserPreferences()
        .then((prefsRes) => {
          const prefs =
            (prefsRes &&
              prefsRes.preferences &&
              typeof prefsRes.preferences === "object")
              ? prefsRes.preferences
              : prefsRes;

          setAuthState((prev) => ({
            ...prev,
            preferences: prefs || {},
            prefsLoading: false,
          }));
          markLoginLatency('preferences_load_complete');
          reportLoginLatencyTrace();
        })
        .catch((err) => {
          setAuthState((prev) => ({
            ...prev,
            prefsError: err?.message || "Failed to load preferences",
            prefsLoading: false,
          }));
          markLoginLatency('preferences_load_complete');
          reportLoginLatencyTrace();
        });
    }
  }, [
    authState.isAuthenticated,
    authState.user,
    authState.preferences,
    authState.prefsError,
    authState.prefsLoading,
  ]);

  const refreshAuth = useCallback(() => checkAuth(), [checkAuth]);

  return (
    <AuthContext.Provider value={{ authState, setAuthState, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
